import { CommonModule } from "@angular/common";
import { Component, inject, signal, OnInit, input, output } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { Container } from "@components/container/container";
import { Exercise as ExerciseComponent } from "@components/exercise/exercise";
import { Feedback } from "@components/feedback/feedback";
import { Header } from "@components/header/header";
import { NewUnblockedRewardCursorModal } from "@components/new-unblocked-reward-cursor-modal/new-unblocked-reward-cursor-modal";
import { ProgressBar } from "@components/progress-bar/progress-bar";
import { RewardCursorProgress } from "@components/reward-cursor-progress/reward-cursor-progress";
import { Answer } from "@domain/Answer";
import { EducationLevelGroup } from "@domain/EducationLevel";
import { Exercise } from "@domain/Exercise";
import { HomophoneGroup } from "@domain/HomophoneGroup";
import { RdxDialogService } from "@radix-ng/primitives/dialog";
import { AnalyticsService } from "@services/analytics.service";
import { ErrorBoundaryService } from "@services/error-boundary.service";
import { ExerciseService } from "@services/exercise.service";
import { RewardService } from "@services/reward.service";
import { SequenceService } from "@services/sequence.service";
import { AuthState } from "@states/auth.state";
import { filter } from "rxjs";

@Component({
  selector: "app-exercises",
  imports: [CommonModule, Header, ProgressBar, Feedback, Container, ExerciseComponent, RewardCursorProgress],
  template: `
    @let currentUser = authState.currentUser();
    @let nextCursorToUnlock = currentUser?.rewardCursors?.getNextCursorToUnlock();
    @let showRewardMechanismToggle = rewardService.isRewardMechanismEnabled() && !!currentUser?.rewardCursors?.getCurrentCursor();

    <app-container>
      <app-header (quitExerciser)="abortSequence.emit()">
        <app-progress-bar class="header__progress-bar" />
      </app-header>
      <div class="exercises card card-medium loading-container" data-testid="exercises">
        @if (isLoading()) {
          <div class="loading-mask">
            <p class="subtitle-3">Chargement...</p>
          </div>
        } @else if (currentExercise(); as exercise) {
          <form id="exercise-form" data-testid="exercise-form" (submit)="handleFormSubmit($event)">
            <app-exercise
              data-testid="exercise"
              [exercise]="exercise"
              [answer]="answer()"
              (playAudio)="handlePlayAudio()"
              (updateAnswer)="handleAnswerUpdate($event)"
            />

            @if (answer(); as answer) {
              @if (answer.isSubmitted()) {
                <app-feedback [answer]="answer" [exercise]="currentExercise()!" />
              }
            }
          </form>
        } @else {
          <div class="loading-mask">
            <p class="subtitle-3">Chargement...</p>
          </div>
        }
      </div>

      <div
        [class]="{
          exercises__actions: true,
          'justify-left-on-mobile': !showRewardMechanismToggle && !!nextCursorToUnlock,
        }"
      >
        @if (currentExercise()) {
          @if (!answer()?.isSubmitted()) {
            <button
              class="submit-answer-button btn btn-primary"
              [disabled]="!answer()"
              type="submit"
              aria-label="Soumettre ma réponse"
              form="exercise-form"
            >
              Soumettre
            </button>
          } @else if (!this.sequenceService.isLastExerciseInSequence()) {
            <button
              class="next-exercise-button btn btn-primary"
              type="button"
              aria-label="Passer à la prochaine question"
              (click)="handleGoToNextExercise()"
            >
              Question suivante
            </button>
          } @else {
            <button class="end-exercise-button btn btn-primary" type="button" aria-label="Terminer l'exercice" (click)="handleSequenceCompletion()">
              Terminer l'exercice
            </button>
          }
        }
      </div>

      @if (nextCursorToUnlock && currentUser) {
        <div class="reward-mechanism__progression">
          <app-reward-cursor-progress
            data-testid="exercise-reward-cursors-progression"
            [rewardCursor]="nextCursorToUnlock"
            [currentStep]="authState.currentUser()?.nextRewardCursorCorrectAnswerCount ?? 0"
            [isNext]="true"
          />
        </div>
      }
    </app-container>
  `,
  styles: `
    @use "responsive" as *;

    .exercises {
      display: flex;
      min-height: 60vh;
      margin: 28px auto;

      @include mobileScreenHeight("small") {
        min-height: 55vh;
      }

      @include screenWidth("from-tablet") {
        min-height: 50vh;
      }

      @include screenWidth("from-desktop") {
        margin: 32px auto;
      }
    }

    #exercise-form {
      display: flex;
      flex: 1;
      flex-direction: column;
      width: 100%;
      margin: 0;
      padding: 0;
    }

    .header__progress-bar {
      width: 100%;
      max-width: none;

      @include screenWidth("from-tablet") {
        width: 60%;
        max-width: 736px;
      }
    }

    .exercises__actions {
      position: absolute;
      display: flex;
      justify-content: center;
      width: 100%;
      z-index: 9960;
      left: 0;
      bottom: 23px;

      &.justify-left-on-mobile {
        @include screenWidth("mobile-only") {
          width: calc(100% - 48px);
          left: 24px;
          justify-content: flex-start;
        }

        > button {
          @include screenWidth("mobile-only") {
            padding: 18px 24px;
          }
        }
      }

      > button {
        display: block;
      }
    }

    .reward-mechanism__progression {
      position: absolute;
      right: 14px;
      bottom: 0px;
      z-index: 9980;
      transform: scale(0.84);

      @include screenWidth("from-tablet") {
        right: 32px;
        transform: scale(0.7);
      }
    }
  `,
})
export class Exercises implements OnInit {
  #modal = inject(RdxDialogService);

  protected authState = inject(AuthState);
  private errorBoundaryService = inject(ErrorBoundaryService);
  private exerciseService = inject(ExerciseService);
  protected analyticsService = inject(AnalyticsService);
  protected sequenceService = inject(SequenceService);
  protected rewardService = inject(RewardService);

  educationLevelGroup = input.required<EducationLevelGroup>();
  homophoneGroups = input.required<HomophoneGroup[]>();

  abortSequence = output();
  showSequenceCompletionModal = output();
  goToErrorReworkMode = output();

  protected currentExercise = signal<Exercise | undefined>(undefined);
  protected isLoading = signal(false);
  protected isExerciseCompleted = signal(false);
  protected feedback = signal<string | undefined>(undefined);
  protected answer = signal<Answer | undefined>(undefined);
  private numberOfClicksOnPlayAudioButton = signal(0);

  constructor() {
    toObservable<number>(this.sequenceService.reworkSessionCount)
      .pipe(
        filter(count => count > 0),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.answer.set(undefined);
        this.goToErrorReworkMode.emit();
        this.getNextExercise().then(() => {
          this.sequenceService.initializeSequence();
        });
      });
  }

  async ngOnInit() {
    this.sequenceService.clearResults();

    try {
      await this.getNextExercise();
      this.sequenceService.initializeSequence();
    } catch {
      this.errorBoundaryService.triggerError();
    }
  }

  async getNextExercise() {
    this.isLoading.set(true);

    try {
      const exercise = await this.fetchNextExercise();
      this.currentExercise.set(exercise);
      this.isLoading.set(false);
    } catch (error) {
      if (error instanceof Error) {
        this.analyticsService.trackException(error);
      }
      this.isLoading.set(false);
      this.errorBoundaryService.triggerError();
    }
  }

  private fetchNextExercise() {
    if (this.sequenceService.isInReworkErrorsMode()) {
      return this.exerciseService.getNextExerciseToRework(this.homophoneGroups(), this.sequenceService.exercisesToRework());
    }

    return this.exerciseService.getNextUnansweredExercise(this.homophoneGroups(), this.sequenceService.sequence());
  }

  handleSequenceCompletion() {
    const numberOfCorrectAnswers = this.sequenceService.numberOfCorrectAnswers;
    const numberOfIncorrectAnswers = this.sequenceService.numberOfIncorrectAnswers;
    this.analyticsService.trackSequenceCompletion(
      this.educationLevelGroup(),
      this.homophoneGroups(),
      numberOfCorrectAnswers,
      numberOfIncorrectAnswers,
      this.sequenceService.isInReworkErrorsMode()
    );
    this.showSequenceCompletionModal.emit();
  }

  async handleGoToNextExercise() {
    await this.getNextExercise();
    this.answer.set(undefined);
    this.numberOfClicksOnPlayAudioButton.set(0);
    this.sequenceService.goToNextExercise();
  }

  handleAnswerUpdate(newAnswer?: Answer) {
    this.answer.set(newAnswer);
  }

  protected handlePlayAudio() {
    const currentExercise = this.currentExercise();

    this.numberOfClicksOnPlayAudioButton.update(currentValue => currentValue + 1);

    if (!currentExercise) return;

    this.analyticsService.trackHearWord(currentExercise.correctAnswer, this.homophoneGroups(), "exercice");
  }
  handleFormSubmit(event: Event) {
    event.preventDefault();
    this.handleSubmitAnswer();
  }

  async handleSubmitAnswer() {
    const currentExercise = this.currentExercise();
    const answer = this.answer();
    if (!currentExercise || !answer) return;

    const newAnswer = currentExercise.submitAnswer(answer);

    this.answer.set(newAnswer);

    this.sequenceService.updateSequence(currentExercise, newAnswer);
    this.analyticsService.trackAnswerSubmitted(
      this.educationLevelGroup(),
      this.homophoneGroups(),
      currentExercise,
      newAnswer,
      this.numberOfClicksOnPlayAudioButton(),
      this.sequenceService.isInReworkErrorsMode()
    );

    if (newAnswer.isCorrect()) {
      const newRewardCursorUnlocked = this.rewardService.incrementCurrentUserCorrectAnswerCount();
      if (newRewardCursorUnlocked) {
        const userCurrentCursor = this.authState.currentUser()?.rewardCursors.getCurrentCursor();

        this.#modal.open({
          content: NewUnblockedRewardCursorModal,
          data: { newRewardCursorUnlocked, userCurrentCursor, selectedEducationLevelGroup: this.educationLevelGroup() },
        });
      }
    }
  }
}
